import { ActorSystem, ErrorActorEvent, sendTo } from 'xstate';
import type { FeeSchedule } from '~/features/employer/billing/types';
import { sendAxiosErrorAlert } from '~/features/utils';
import { ALERT_VIEW_STATE } from '~/features/xstateSystem';
import { RequestDeadlineError } from '~/utils/requestDeadline';
import { ExistingInquiryError } from '../initialInquiryReceipt';
import { listingErrorAlert, listingErrorCode } from '../listings/listingConflicts';

type ErrorAlertEvent = ReturnType<typeof sendAxiosErrorAlert>;

const responseStatus = (error: unknown): number | undefined => {
  if (error == null || typeof error !== 'object' || !('response' in error)) return undefined;
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
};

const isScreeningUnavailable = (error: unknown): boolean => {
  if (responseStatus(error) !== 503) return false;
  const response = (error as { response?: { data?: { extra?: { code?: unknown } } } }).response;
  return response?.data?.extra?.code === 'MESSAGE_SCREENING_UNAVAILABLE';
};

const billingCode = (error: unknown): string | null => {
  const response = (error as { response?: { data?: { extra?: { code?: unknown } } } }).response;
  const code = response?.data?.extra?.code;
  return typeof code === 'string' ? code : null;
};

const isBillingUnavailable = (error: unknown): boolean =>
  responseStatus(error) === 503 && billingCode(error) === 'BILLING_UNAVAILABLE';

export const isPaymentMethodRequired = (error: unknown): boolean =>
  responseStatus(error) === 402 && billingCode(error) === 'PAYMENT_METHOD_REQUIRED';

export type FeeRefusalCode = 'FEE_AUTHORIZATION_REQUIRED' | 'FEE_SCHEDULE_CHANGED';

/**
 * [MCV-1858] The accept was refused because the employer has not authorized the platform-fee
 * schedule (428) or authorized one that has since changed (409). Both are recoverable by showing
 * the (refetched) schedule again.
 */
export const feeRefusalCode = (error: unknown): FeeRefusalCode | null => {
  const status = responseStatus(error);
  if (status !== 428 && status !== 409) return null;
  const code = billingCode(error);
  if (status === 428 && code === 'FEE_AUTHORIZATION_REQUIRED') return code;
  if (status === 409 && code === 'FEE_SCHEDULE_CHANGED') return code;
  return null;
};

/**
 * [MCV-1933] A refused fee authorization carries the freshly computed schedule in `extra`. It is
 * the only place an application accepted on the listing's terms can learn its schedule, because
 * the offer (and so the schedule) does not exist until that accept is attempted.
 */
export const feeRefusalSchedule = (error: unknown): FeeSchedule | null => {
  if (feeRefusalCode(error) === null) return null;
  const response = (error as { response?: { data?: { extra?: { feeSchedule?: unknown } } } })
    .response;
  const schedule = response?.data?.extra?.feeSchedule;
  return schedule != null &&
    typeof schedule === 'object' &&
    typeof (schedule as { scheduleHash?: unknown }).scheduleHash === 'string'
    ? (schedule as FeeSchedule)
    : null;
};

/**
 * [MCV-1858] The RESPONDING employer's accept was refused because the listing owner (the payer)
 * has not authorized the current fee schedule (409). The responder cannot authorize it, so this is
 * deliberately not a `FeeRefusalCode`: it never opens the fee dialog. The server clears the payer's
 * acceptance, so the view reloads the response and tells the responder to wait.
 */
export const isPayerFeeAuthorizationRequired = (error: unknown): boolean =>
  responseStatus(error) === 409 && billingCode(error) === 'PAYER_FEE_AUTHORIZATION_REQUIRED';

// [MCV-1889] Who pays follows the listing's side: the listing owner on a buying listing, the
// requesting employer on a selling one. "The paying employer" is right on both.
export const PAYER_FEE_AUTHORIZATION_REQUIRED_MESSAGE =
  'The paying employer needs to review and accept the current fee schedule before this can be accepted.';

export type AcceptConflictCode = 'LISTING_FILLED' | 'RETRY' | 'CANNOT_ACCEPT_OWN_OFFER';

// [MCV-1889] Accepting can lose a race: another response on the same listing became the contract
// (LISTING_FILLED), or a concurrent write held the same rows (RETRY). A party also cannot accept the
// offer it sent while the other side has not (CANNOT_ACCEPT_OWN_OFFER). The code may sit at the top
// of the body or under `extra`, like the billing codes above.
export const acceptConflictCode = (error: unknown): AcceptConflictCode | null => {
  if (responseStatus(error) !== 409) return null;
  const data = (error as { response?: { data?: { code?: unknown; extra?: { code?: unknown } } } })
    .response?.data;
  const code = data?.extra?.code ?? data?.code;
  return code === 'LISTING_FILLED' || code === 'RETRY' || code === 'CANNOT_ACCEPT_OWN_OFFER'
    ? code
    : null;
};

export const LISTING_FILLED_ACCEPT_MESSAGE = 'This listing has already been filled';
export const ACCEPT_RETRY_MESSAGE = 'Something changed at the same time. Refresh and try again.';
export const CANNOT_ACCEPT_OWN_OFFER_MESSAGE =
  'You cannot accept your own offer. Wait for the other party to respond.';

const acceptConflictMessage = (error: unknown): string | null => {
  const code = acceptConflictCode(error);
  if (code === 'LISTING_FILLED') return LISTING_FILLED_ACCEPT_MESSAGE;
  if (code === 'CANNOT_ACCEPT_OWN_OFFER') return CANNOT_ACCEPT_OWN_OFFER_MESSAGE;
  return code === 'RETRY' ? ACCEPT_RETRY_MESSAGE : null;
};

export const FEE_SCHEDULE_CHANGED_MESSAGE = 'The fee schedule changed. Review it and accept again.';
export const FEE_AUTHORIZATION_REQUIRED_MESSAGE =
  'Review and authorize the fee schedule before accepting this offer.';

export const contentScreeningFeedback = (error: unknown): string | null => {
  if (responseStatus(error) !== 422) return null;
  const response = (error as { response?: { data?: { extra?: { code?: unknown } } } }).response;
  return response?.data?.extra?.code === 'MESSAGE_CONTENT_FILTERED'
    ? 'Message not sent. Content screening rejected this text. Rewrite the message or commentary before sending again. Your draft is saved.'
    : null;
};

const SCREENING_UNAVAILABLE_TEXT =
  'Message screening is temporarily unavailable. Your draft is saved; try again shortly.';
/** Refusal copy when content screening is down, so users do not rewrite innocent text. */
const screeningUnavailableFeedback = (error: unknown): string | null =>
  isScreeningUnavailable(error) ? `Message not sent. ${SCREENING_UNAVAILABLE_TEXT}` : null;

export const MASKED_CONTACT_REFUSAL =
  'Message not sent. Review your draft and try again. Contact information cannot be shared while masked.';

export const isMaskedContactRefusal = (error: unknown): boolean => {
  if (responseStatus(error) !== 422) return false;
  const response = (error as { response?: { data?: { extra?: { pii_types?: unknown } } } })
    .response;
  return (
    Array.isArray(response?.data?.extra?.pii_types) && response.data.extra.pii_types.length > 0
  );
};

const maskedContactFeedback = (error: unknown): string | null =>
  isMaskedContactRefusal(error) ? MASKED_CONTACT_REFUSAL : null;

/** Why a reply was refused: screening rejection, screening outage, masked contact, or `fallback`. */
export const replyRefusalFeedback = (error: unknown, fallback: string): string =>
  contentScreeningFeedback(error) ??
  screeningUnavailableFeedback(error) ??
  maskedContactFeedback(error) ??
  fallback;

export const shouldKeepMessageEditorOpen = (event: { error?: unknown }): boolean => {
  if (event.error instanceof ExistingInquiryError) return true;
  const status = responseStatus(event.error);
  return status === 400 || status === 422 || isScreeningUnavailable(event.error);
};

export const isUncertainListingActionError = (error: unknown): boolean => {
  if (error instanceof ExistingInquiryError) return false;
  if (isScreeningUnavailable(error)) return false;
  if (isBillingUnavailable(error)) return false;
  if (isPaymentMethodRequired(error)) return false;
  if (feeRefusalCode(error)) return false;
  if (isPayerFeeAuthorizationRequired(error)) return false;
  const status = responseStatus(error);
  return status === undefined || status >= 500;
};

export const listingActionRefusalMessage = (error: unknown): string | null => {
  if (isBillingUnavailable(error))
    return 'Billing is not configured for this environment, so offers cannot be accepted yet. Nothing was changed.';
  if (isPaymentMethodRequired(error))
    return 'Add a payment method in Billing settings before accepting this offer. Nothing was changed.';
  if (isPayerFeeAuthorizationRequired(error)) return PAYER_FEE_AUTHORIZATION_REQUIRED_MESSAGE;
  const feeCode = feeRefusalCode(error);
  if (feeCode === 'FEE_SCHEDULE_CHANGED') return FEE_SCHEDULE_CHANGED_MESSAGE;
  if (feeCode === 'FEE_AUTHORIZATION_REQUIRED') return FEE_AUTHORIZATION_REQUIRED_MESSAGE;
  return acceptConflictMessage(error);
};

export const formatListingActionError = (event: { error?: unknown }): ErrorAlertEvent => {
  const status = responseStatus(event.error);
  const contentFeedback = contentScreeningFeedback(event.error);
  if (contentFeedback) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: { text: 'Message not sent', subText: contentFeedback, type: 'error', timeout: 5000 },
    };
  }

  if (event.error instanceof ExistingInquiryError) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Message not sent',
        subText: event.error.message,
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (isScreeningUnavailable(event.error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Message not sent',
        subText: SCREENING_UNAVAILABLE_TEXT,
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (event.error instanceof RequestDeadlineError) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: "We couldn't confirm whether this completed",
        subText: 'Check the current response before retrying your original submission.',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (isBillingUnavailable(event.error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Offer not accepted',
        subText: listingActionRefusalMessage(event.error) ?? '',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (isPaymentMethodRequired(event.error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Payment method required',
        subText: listingActionRefusalMessage(event.error) ?? '',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (feeRefusalCode(event.error) || isPayerFeeAuthorizationRequired(event.error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Offer not accepted',
        subText: listingActionRefusalMessage(event.error) ?? '',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  // [MCV-1889] A request for a seller's labor can be refused because selling is not on for the
  // employer (403), or because it attached providers (400, the roster is what is on offer).
  const sellSideCode = listingErrorCode(event.error);
  if (sellSideCode === 'FEATURE_DISABLED' || sellSideCode === 'PROVIDER_IDS_NOT_ALLOWED') {
    return listingErrorAlert(event);
  }

  if (acceptConflictCode(event.error)) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Not completed',
        subText: acceptConflictMessage(event.error) ?? '',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (status != null && status >= 500) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Unable to complete response',
        subText: 'Please try again. If the problem continues, contact support.',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  if (status === 409) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'This listing already has a response',
        subText: 'Reload the listing to view the current response before trying another action.',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  const fallback = sendAxiosErrorAlert(event);

  if (status === 422) {
    return {
      type: 'ON_SHOW_ALERT',
      payload: {
        text: 'Review this response',
        subText: fallback.payload.subText || 'Check the response details and try submitting again.',
        type: 'error',
        timeout: 5000,
      },
    };
  }

  return fallback;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sendListingActionError: any = sendTo(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ system }: { system: ActorSystem<any> }) => system?.get(ALERT_VIEW_STATE),
  ({ event }: { event: ErrorActorEvent<unknown> }) => formatListingActionError(event)
);
